import { makeStyles } from '@material-ui/core/styles';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { color } from '../../app/theme';
import { CustomerConditionType } from '../../app/types';

const useStyles = makeStyles(
  {
    root: {
      paddingBottom: 24,
    },
    line: {
      flexGrow: 1,
      borderBottom: `1px solid ${color.$9}`,
    },
    text: {
      margin: '0 16px',
      maxWidth: '80%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      letterSpacing: 'normal',
      color: color.$11,
    },
    order: { display: 'flex', alignItems: 'center', marginBottom: 24 },
    condition: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    valueBlock: {
      display: 'flex',
      gap: 8,
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'wrap',
      fontWeight: '500',
      fontSize: '13px',
      lineHeight: '18.2px',
      color: color.$13,
    },
    value: {
      display: '-webkit-box',
      overflow: 'hidden',
      wordBreak: 'break-all',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: '1',
      '& > span': {
        margin: '0 4px',
      },
    },
    or: {
      fontWeight: '400',
    },
    and: {
      fontWeight: '400',
      width: '100%',
      textAlign: 'center',
    },
  },
  {
    name: 'PreviewCustomerConditionNode',
  },
);

const PreviewCustomerConditionNode = ({ node }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const nodes = useSelector(store => store.ma.data?.nodes) || [];
  const { type, groupId } = node?.data || {};

  const groupNode = useMemo(
    () => nodes.find(n => n?.id === groupId) || {},
    [nodes],
  );
  const { triggerType, triggerCondition } = groupNode?.data || {};

  const contentComponent = useMemo(() => {
    switch (triggerType) {
      case CustomerConditionType.TAG:
        return triggerCondition.reduce((acc, tag, index) => {
          const { type: tagType, tags = [], count = 0 } = tag;

          if (index > 0) {
            acc.push(
              <div key={`or-${index}`} className={classes.and}>
                且
              </div>,
            );
          }

          switch (tagType) {
            case 'include':
              acc.push(
                <div key={`location-${index}`} className={classes.value}>
                  {t('tagCondition.include')}
                  {tags.join('、')}，{t('tagCondition.andTaging')}
                  <span>{count}</span>
                  {t('tagCondition.count')}
                </div>,
              );
              break;
            case 'exclude':
              acc.push(
                <div key={`location-${index}`} className={classes.value}>
                  {t('tagCondition.exclude')}
                  {tags.join('、')}
                </div>,
              );
              break;
            case 'anyGreater':
              acc.push(
                <div key={`location-${index}`} className={classes.value}>
                  {t('tagCondition.someTagCount')}
                  <span>{count}</span>
                  {t('tagCondition.count')}
                </div>,
              );
              break;
            case 'none':
              acc.push(
                <div key={`location-${index}`} className={classes.value}>
                  {t('tagCondition.noTag')}
                </div>,
              );
              break;
            default:
              break;
          }

          return acc;
        }, []);
      case CustomerConditionType.BIRTHDAY:
        return (
          <div className={classes.value}>
            {t(`birthday.${triggerCondition}`)}
          </div>
        );
      case CustomerConditionType.GENDER:
        return (
          <div className={classes.value}>{t(`gender.${triggerCondition}`)}</div>
        );
      case CustomerConditionType.LOCATION:
        return triggerCondition.reduce((acc, location, index) => {
          if (index > 0) {
            acc.push(
              <div key={`or-${index}`} className={classes.or}>
                或
              </div>,
            );
          }
          acc.push(
            <div key={`location-${index}`} className={classes.value}>
              {location}
            </div>,
          );
          return acc;
        }, []);
      default:
        return null;
    }
  }, [groupNode?.data]);

  return (
    <div className={classes.root}>
      <div className={classes.order}>
        <span className={classes.line} />
        <span className={classes.text}>
          <div className={classes.condition}>
            {t(`customerConditionNode.${type}`, {
              triggerType: t(`customerConditionNode.${triggerType}`),
            })}
          </div>
        </span>
        <span className={classes.line} />
      </div>
      <div className={classes.valueBlock}>{contentComponent}</div>
    </div>
  );
};

export default PreviewCustomerConditionNode;
